import TaxiCorporationPlanEntryBillingsDownloadParams from "../../../../../common/services/taxi-corporation/plan-entry-billings/download/taxi-corporation-plan-entry-billings-download-params";
import BillingModel from "../../../../../common/types/billing-model";
import TaxiCorporationSortKey from "../../../../../common/types/taxi-corporation-sort-key";
import BillingsTaxiRouteListingFilter, {
  BillingsTaxiRouteListingCargoOrderInternalIdFilter,
  BillingsTaxiRouteListingDriverFilter,
  BillingsTaxiRouteListingFleetPartnerFilter,
  BillingsTaxiRouteListingRouteIdFilter,
  BillingsTaxiRouteListingRouteStartDateFilter,
} from "./types/billings-taxi-route-listing-filter";
import BillingsTaxiRouteListingFilterType from "./types/billings-taxi-route-listing-filter-type";
import BillingsTaxiRouteListingSortKey from "./types/billings-taxi-route-listing-sort-key";

const createRequestSortKey = (
  sortKey: BillingsTaxiRouteListingSortKey | null,
): TaxiCorporationSortKey | undefined => {
  const options: {
    sortKey: BillingsTaxiRouteListingSortKey;
    requestSortKey: TaxiCorporationSortKey;
  }[] = [
    {
      requestSortKey: TaxiCorporationSortKey.DISTANCE_DESC,
      sortKey: BillingsTaxiRouteListingSortKey.DISTANCE_DESC,
    },
    {
      requestSortKey: TaxiCorporationSortKey.DISTANCE_ASC,
      sortKey: BillingsTaxiRouteListingSortKey.DISTANCE_ASC,
    },
    {
      requestSortKey: TaxiCorporationSortKey.HIGHWAY_CHARGE_DESC,
      sortKey: BillingsTaxiRouteListingSortKey.TOLL_ROADS_COST_DESC,
    },
    {
      requestSortKey: TaxiCorporationSortKey.HIGHWAY_CHARGE_ASC,
      sortKey: BillingsTaxiRouteListingSortKey.TOLL_ROADS_COST_ASC,
    },
    {
      requestSortKey: TaxiCorporationSortKey.HUMAN_ID_ASC,
      sortKey: BillingsTaxiRouteListingSortKey.ROUTE_ID_ASC,
    },
    {
      requestSortKey: TaxiCorporationSortKey.HUMAN_ID_DESC,
      sortKey: BillingsTaxiRouteListingSortKey.ROUTE_ID_DESC,
    },
    {
      requestSortKey: TaxiCorporationSortKey.DRIVER_ASC,
      sortKey: BillingsTaxiRouteListingSortKey.DRIVER_ASC,
    },
    {
      requestSortKey: TaxiCorporationSortKey.DRIVER_DESC,
      sortKey: BillingsTaxiRouteListingSortKey.DRIVER_DESC,
    },
    {
      requestSortKey: TaxiCorporationSortKey.BILLING_MODEL_ASC,
      sortKey: BillingsTaxiRouteListingSortKey.BILLING_MODEL_ASC,
    },
    {
      requestSortKey: TaxiCorporationSortKey.BILLING_MODEL_DESC,
      sortKey: BillingsTaxiRouteListingSortKey.BILLING_MODEL_DESC,
    },
    {
      requestSortKey: TaxiCorporationSortKey.DISTANCE_RATE_ASC,
      sortKey: BillingsTaxiRouteListingSortKey.DISTANCE_RATE_ASC,
    },
    {
      requestSortKey: TaxiCorporationSortKey.DISTANCE_RATE_DESC,
      sortKey: BillingsTaxiRouteListingSortKey.DISTANCE_RATE_DESC,
    },
    {
      requestSortKey: TaxiCorporationSortKey.DISTANCE_AMOUNT_ASC,
      sortKey: BillingsTaxiRouteListingSortKey.DISTANCE_COST_ASC,
    },
    {
      requestSortKey: TaxiCorporationSortKey.DISTANCE_AMOUNT_DESC,
      sortKey: BillingsTaxiRouteListingSortKey.DISTANCE_COST_DESC,
    },
    {
      requestSortKey: TaxiCorporationSortKey.TOTAL_AMOUNT_ASC,
      sortKey: BillingsTaxiRouteListingSortKey.TOTAL_COST_ASC,
    },
    {
      requestSortKey: TaxiCorporationSortKey.TOTAL_AMOUNT_DESC,
      sortKey: BillingsTaxiRouteListingSortKey.TOTAL_COST_DESC,
    },
  ];

  return options.find((option) => option.sortKey === sortKey)?.requestSortKey;
};

const createRequest = (
  taxiCorporationUuid: string,
  filters: BillingsTaxiRouteListingFilter[],
  sortKey: BillingsTaxiRouteListingSortKey | null,
): TaxiCorporationPlanEntryBillingsDownloadParams => {
  return {
    taxiCorporationUuid: taxiCorporationUuid,
    sortKey: createRequestSortKey(sortKey),
    humanId: (
      filters.find(
        (filter) => filter.type === BillingsTaxiRouteListingFilterType.ROUTE_ID,
      )?.value as BillingsTaxiRouteListingRouteIdFilter["value"] | undefined
    )?.toString(),
    cargoOrderHumanId: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiRouteListingFilterType.CARGO_ORDER_INTERNAL_ID,
    )?.value as
      | BillingsTaxiRouteListingCargoOrderInternalIdFilter["value"]
      | undefined,
    driver: filters.find(
      (filter) => filter.type === BillingsTaxiRouteListingFilterType.DRIVER,
    )?.value as BillingsTaxiRouteListingDriverFilter["value"] | undefined,
    dateSince: (
      filters.find(
        (filter) =>
          filter.type === BillingsTaxiRouteListingFilterType.ORDER_START_DATE,
      )?.value as
        | BillingsTaxiRouteListingRouteStartDateFilter["value"]
        | undefined
    )?.from.toJSON(),
    dateTo: (
      filters.find(
        (filter) =>
          filter.type === BillingsTaxiRouteListingFilterType.ORDER_START_DATE,
      )?.value as
        | BillingsTaxiRouteListingRouteStartDateFilter["value"]
        | undefined
    )?.to.toJSON(),
    billingModel: filters.find(
      (filter) =>
        filter.type === BillingsTaxiRouteListingFilterType.BILLING_MODEL,
    )?.value as BillingModel | undefined,
    fleetPartner: filters.find(
      (filter) =>
        filter.type === BillingsTaxiRouteListingFilterType.FLEET_PARTNER,
    )?.value as BillingsTaxiRouteListingFleetPartnerFilter["value"] | undefined,
  };
};

const billingsTaxiRouteListingReportRequestFactory = {
  createRequest,
};

export default billingsTaxiRouteListingReportRequestFactory;
